import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, Grid, Stack, Divider } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";

import { ExamDateDto, ExamFormat } from "api";
import { RegistrationStepPage, Table, Select } from "components";
import { useRegistrationJourney } from "journey";
import { GTag } from "hooks";
import { getColumns } from "./columns";
import { useFilterOptions } from "./useFilterOptions";
import { useFilteredExams } from "./useFilteredExams";

export const SelectExam = () => {
  const { t } = useTranslation();
  const {
    examGroupOptions,
    centreOptions,
    isCentreOptionsFetching,
    isExamGroupOptionsFetching,
  } = useFilterOptions();
  const { rows, filters, isFetching, setFilters } = useFilteredExams();
  const { selectExam } = useRegistrationJourney();

  const columns = useMemo(
    () =>
      getColumns(t, (exam: ExamDateDto) => {
        selectExam(exam);
        GTag.addToCart(exam);
      }),
    [selectExam, t]
  );

  return (
    <RegistrationStepPage subTitle={t("selectExam.title")} stepIndex={0}>
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <Typography variant="h3">{t("selectExam.filters.title")}</Typography>
          <Button size="medium" color="error" onClick={() => setFilters({})}>
            <ReplayIcon /> {t("selectExam.filters.reset")}
          </Button>
        </Stack>
        <Divider sx={{ mt: 1 }} />
      </Box>

      <Grid container spacing={{ xs: 3, md: 4, xl: 4 }} sx={{ mb: 8 }}>
        <Grid size={{ xs: 12, md: 6, xl: 3 }}>
          <Select
            label={t("selectExam.filters.examLabel")}
            subLabel={t("selectExam.filters.examSubLabel")}
            value={filters.examGroupId}
            options={examGroupOptions}
            loading={isExamGroupOptionsFetching}
            onChange={(examGroupId) => setFilters({ ...filters, examGroupId })}
            anyOption
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, xl: 3 }}>
          <Select
            label={t("selectExam.filters.centreLabel")}
            subLabel={t("selectExam.filters.centreSubLabel")}
            value={filters.centreCode}
            options={centreOptions}
            loading={isCentreOptionsFetching}
            onChange={(centreCode) => setFilters({ ...filters, centreCode })}
            anyOption
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, xl: 3 }}>
          <Select
            label={t("selectExam.filters.formatLabel")}
            subLabel={t("selectExam.filters.formatSubLabel")}
            value={filters.examFormat}
            options={[
              { value: ExamFormat.Pb, label: t("common.paperBased") },
              { value: ExamFormat.Cd, label: t("common.computerBased") },
            ]}
            onChange={(examFormat) => setFilters({ ...filters, examFormat })}
            anyOption
          />
        </Grid>
      </Grid>

      <Table
        columns={columns}
        data={rows}
        initialState={{ sorting: [{ id: "examDate", desc: false }] }}
        loading={isFetching}
        onVisibleRowsChange={(visibleRows) => {
          if (visibleRows.length > 0) {
            GTag.viewExamList(visibleRows);
          }
        }}
      />
    </RegistrationStepPage>
  );
};
